<script setup lang="ts">
import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import { buildCategoryLevelUrl } from '@/shared/utils/buildCategoryLevelUrl';
import { typeguardForString } from '@/shared/utils/typeguardForString';
import type {
  ILawyerCategoriesProps,
  ILawyerCategoriesEmits,
  ILawyerCategory,
} from '@/entities/lawyer/model/types/lawyerCategories';

const props = withDefaults(defineProps<ILawyerCategoriesProps>(), {
  categories: () => [],
  tag: 'NuxtLink',
  isCollapsible: false,
  maxVisibleCategories: 5,
});

const emit = defineEmits<ILawyerCategoriesEmits>();

const route = useRoute();
const nuxtLinkComponent = resolveComponent('NuxtLink');

const isShowFull: Ref<boolean> = ref(false);

const isCatalogPage: ComputedRef<boolean> = computed(() =>
  route.path.startsWith(ROOT_CATALOG_URL),
);
const isLawyerDetailPage: ComputedRef<boolean> = computed(
  () => route.path.includes('/lawyers/') || route.path.includes('/organizations/'),
);
const isShowMoreButton: ComputedRef<boolean> = computed(
  () => isCatalogPage.value || isLawyerDetailPage.value || props.isCollapsible,
);
const isStaticTags: ComputedRef<boolean> = computed(
  () => isLawyerDetailPage.value && props.isAuthorizedNonUser,
);

const resolvedTag = computed(() =>
  props.tag === 'NuxtLink' ? nuxtLinkComponent : props.tag,
);

const filterCategories = (slug: string): ILawyerCategory[] => {
  const currentItem = props.categories.find((s) => s?.slug === slug);
  const filteredItems = props.categories.filter(
    (item) => item?.slug !== currentItem?.slug,
  );
  if (currentItem) filteredItems.unshift(currentItem);
  return filteredItems;
};

const modifiedCategories = computed<ILawyerCategory[]>(() => {
  const subcategory = typeguardForString(route.params.subcategory);
  if (subcategory) return filterCategories(subcategory);
  return props.categories;
});

const isLimited: ComputedRef<boolean> = computed(
  () =>
    isShowMoreButton.value &&
    !isShowFull.value &&
    modifiedCategories.value.length > props.maxVisibleCategories,
);

const currentCategories: ComputedRef<ILawyerCategory[]> = computed(() =>
  isLimited.value
    ? modifiedCategories.value.slice(0, props.maxVisibleCategories)
    : modifiedCategories.value,
);

const routeQuery: ComputedRef<string> = computed(
  () => route.fullPath.split('?')[1] || '',
);

const showAllCategories = (): void => {
  isShowFull.value = true;
};

const buildLevelLink = (itemSlug: string, level: 'category' | 'subcategory'): string => {
  const path = buildCategoryLevelUrl({
    level,
    itemSlug,
    fullPath: route.fullPath,
    region: typeguardForString(route.params.region),
    category: props.categorySlug || typeguardForString(route.params.category),
    subcategory: typeguardForString(route.params.subcategory),
    sort: typeguardForString(route.params.sort),
  });

  if (path === route.path) return '';
  return routeQuery.value ? `${path}?${routeQuery.value}` : path;
};

const categoryLink = (itemSlug: string): string => buildLevelLink(itemSlug, 'category');
const subcategoryLink = (itemSlug: string): string =>
  buildLevelLink(itemSlug, 'subcategory');

const clickHandler = (): void => {
  emit('click');
};
</script>

<template>
  <ul :class="['lawyer-categories', { _static: isStaticTags }]">
    <li
      v-for="category in currentCategories"
      :key="category.id"
      class="lawyer-categories__item"
    >
      <component
        :is="resolvedTag"
        :class="['lawyer-categories__name', { _button: tag === 'button' }]"
        :to="
          category?.parent_id
            ? subcategoryLink(category?.slug)
            : categoryLink(category?.slug)
        "
        @click.prevent="clickHandler"
      >
        <span>
          {{ category.name }}
        </span>
      </component>
    </li>
    <li v-if="isLimited">
      <button
        class="lawyer-categories__button"
        aria-label="Ещё: показать все категории"
        @click.prevent="showAllCategories"
      >
        Ещё...
      </button>
    </li>
  </ul>
</template>

<style lang="less">
.lawyer-categories {
  display: inline-flex;
  flex-wrap: wrap;
  gap: @main-size;

  &__item {
    background-color: @color-black-haze;
    display: inline-flex;
    border-radius: @main-size;
    font-weight: 600;
    font-size: @size-x3;
    line-height: @size-x4;
    transition: 0.3s background-color linear;
  }

  &:not(._static) &__item:hover {
    background: darken(@color-black-haze, 10%);
  }

  &__name {
    display: block;
    padding: 5px;
    min-height: 26px;
    text-align: left;

    &._button {
      font-size: inherit;
      line-height: inherit;
      font-weight: 600;
      padding: 5px;
    }
  }

  &._static &__name._button {
    cursor: auto;
  }

  &__button {
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 600;
    background: @color-black-haze;
    border: 1px solid @color-elephant;
    border-radius: @main-size;
    padding: @main-size 10px;
    max-height: 26px;
    transition: 0.3s background-color linear;

    &:hover {
      background: darken(@color-black-haze, 10%);
    }
  }
}
</style>
